/**
 * ProtocolMedia API Service
 * Handles dental protocol media documentation with S3 uploads
 */

import axios from 'axios';
import { createS3UploadConfig, validatePresignedUrl } from '@/utils/s3Utils';

class ProtocolMediaService {
  constructor() {
    this.baseUrl = '/api/protocolMedia';
  }

  /**
   * Get base URL for protocol media endpoints
   * @param {string} clinicURL - Clinic URL from route params
   * @param {string} branchURL - Branch URL from route params  
   * @param {string} patientId - Patient ID
   * @returns {string} Base URL for API calls
   */
  getBaseUrl(clinicURL, branchURL, patientId) {
    return `${this.baseUrl}/${clinicURL}/${branchURL}/${patientId}`;
  }

  /**
   * Create presigned URLs for batch photo upload
   * @param {string} clinicURL - Clinic URL
   * @param {string} branchURL - Branch URL
   * @param {string} patientId - Patient ID
   * @param {Object} uploadData - Upload request data
   * @returns {Promise<Object>} Presigned URLs and protocol progress
   */
  async createBatchPresignedUrls(clinicURL, branchURL, patientId, uploadData) {
    const url = this.getBaseUrl(clinicURL, branchURL, patientId);
    
    // Validate file metadata
    this.validateFilesMetadata(uploadData.filesMetadata);
    
    try {
      const response = await axios.post(url, uploadData);
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to create presigned URLs');
      throw error;
    }
  }

  /**
   * Update existing protocol media with new presigned URL
   * @param {string} clinicURL - Clinic URL
   * @param {string} branchURL - Branch URL
   * @param {string} patientId - Patient ID
   * @param {string} mediaFileId - Media file ID to update
   * @param {Object} updateData - Update request data
   * @returns {Promise<Object>} Updated presigned URL and edit info
   */
  async updatePresignedUrl(clinicURL, branchURL, patientId, mediaFileId, updateData) {
    const url = `${this.getBaseUrl(clinicURL, branchURL, patientId)}/${mediaFileId}`;
    
    // Validate file data
    this.validateFile(updateData);
    
    try {
      const response = await axios.put(url, updateData);
      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error('Edit window expired. Protocol media can only be modified within 24 hours of creation.');
      }
      this.handleApiError(error, 'Failed to update presigned URL');
      throw error;
    }
  }

  /**
   * Get list of protocol media for patient
   * @param {string} clinicURL - Clinic URL
   * @param {string} branchURL - Branch URL
   * @param {string} patientId - Patient ID
   * @param {Object} options - Query options (offset, limit, sort, etc.)
   * @returns {Promise<Object>} Media files list and pagination
   */
  async getProtocolMediaList(clinicURL, branchURL, patientId, options = {}) {
    const url = this.getBaseUrl(clinicURL, branchURL, patientId);
    
    const params = {
      offset: options.offset || 0,
      limit: options.limit || 10,
      sort: options.sort || 'creationDt',
      order: options.order || 'DESC',
      ...(options.labId && { labId: options.labId }),
      ...(options.softDeleted && { softDeleted: options.softDeleted })
    };
    
    try {
      const response = await axios.get(url, { params });
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'Failed to fetch protocol media list');
      throw error;
    }
  }

  /**
   * Get single protocol media details
   * @param {string} clinicURL - Clinic URL
   * @param {string} branchURL - Branch URL
   * @param {string} patientId - Patient ID
   * @param {string} mediaFileId - Media file ID
   * @returns {Promise<Object>} Media details
   */
  async getProtocolMedia(clinicURL, branchURL, patientId, mediaFileId) {
    const url = `${this.getBaseUrl(clinicURL, branchURL, patientId)}/${mediaFileId}`;
    
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Protocol media not found for this patient');
      }
      this.handleApiError(error, 'Failed to fetch protocol media');
      throw error;
    }
  }

  /**
   * Upload file to S3 using presigned URL
   * @param {string} presignedUrl - S3 presigned URL
   * @param {File} file - File to upload
   * @param {Function} onProgress - Progress callback (optional)
   * @returns {Promise<void>}
   */
  async uploadToS3(presignedUrl, file, onProgress) {
    // Validate presigned URL before attempting upload
    if (!validatePresignedUrl(presignedUrl)) {
      throw new Error('Invalid or expired presigned URL');
    }
    
    try {
      // Create proper S3 upload config with all required headers
      const config = createS3UploadConfig(presignedUrl, file, onProgress);
      
      // Upload file directly (not FormData) to match signature
      await axios.put(presignedUrl, file, config);
    } catch (error) {
      console.error('S3 upload failed:', error);
      
      // Provide more detailed error information
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;
        
        if (status === 403 && errorData.includes('SignatureDoesNotMatch')) {
          throw new Error('S3 signature mismatch - please regenerate presigned URL');
        } else if (status === 403) {
          throw new Error('S3 upload forbidden - check permissions and URL validity');
        } else {
          throw new Error(`S3 upload failed with status ${status}: ${errorData}`);
        }
      } else {
        throw new Error('Failed to upload file to S3 - network or connection error');
      }
    }
  }

  /**
   * Batch upload multiple files to S3
   * @param {Array<Object>} uploadTasks - Array of {presignedUrl, file, onProgress}
   * @returns {Promise<Array>} Array of upload results
   */
  async batchUploadToS3(uploadTasks) {
    const uploadPromises = uploadTasks.map(async (task, index) => {
      try {
        await this.uploadToS3(task.presignedUrl, task.file, task.onProgress);
        return { index, success: true, error: null };
      } catch (error) {
        console.error(`Upload failed for file ${index}:`, error);
        return { index, success: false, error: error.message };
      }
    });
    
    return Promise.all(uploadPromises);
  }

  /**
   * Validate files array for batch upload
   * @param {Array<Object>} files - Files array
   * @throws {Error} If validation fails
   */
  validateFiles(files) {
    if (!Array.isArray(files) || files.length === 0) {
      throw new Error('Files array is required and must be non-empty');
    }
    
    if (files.length > 5) {
      throw new Error('Maximum 5 files per batch upload');
    }
    
    let totalSize = 0;
    
    files.forEach((file, index) => {
      // Check if file has view_details (new format) or not (old format)
      if (file.view_details) {
        this.validateFileMetadata(file, `File ${index + 1}`);
      } else {
        this.validateFile(file, `File ${index + 1}`);
      }
      totalSize += file.fileSize;
    });
    
    if (totalSize > 25 * 1024 * 1024) { // 25MB
      throw new Error('Total batch size exceeds 25MB limit');
    }
  }

  /**
   * Validate files metadata array for batch upload
   * @param {Array<Object>} filesMetadata - Files metadata array
   * @throws {Error} If validation fails
   */
  validateFilesMetadata(filesMetadata) {
    if (!Array.isArray(filesMetadata) || filesMetadata.length === 0) {
      throw new Error('Files metadata array is required and must be non-empty');
    }
    
    if (filesMetadata.length > 5) {
      throw new Error('Maximum 5 files per batch upload');
    }
    
    let totalSize = 0;
    
    filesMetadata.forEach((fileMetadata, index) => {
      this.validateFileMetadata(fileMetadata, `File ${index + 1}`);
      totalSize += fileMetadata.fileSize;
    });
    
    if (totalSize > 25 * 1024 * 1024) { // 25MB
      throw new Error('Total batch size exceeds 25MB limit');
    }
  }

  /**
   * Validate single file metadata
   * @param {Object} fileMetadata - File metadata object
   * @param {string} prefix - Error message prefix
   * @throws {Error} If validation fails
   */
  validateFileMetadata(fileMetadata, prefix = 'File') {
    if (!fileMetadata.fileName) {
      throw new Error(`${prefix}: fileName is required`);
    }

    if (!fileMetadata.contentType) {
      throw new Error(`${prefix}: contentType is required`);
    }

    if (!fileMetadata.fileType) {
      throw new Error(`${prefix}: fileType is required`);
    }
    
    if (!['image/jpeg', 'image/png'].includes(fileMetadata.fileType)) {
      throw new Error(`${prefix}: Only JPEG and PNG formats are allowed`);
    }
    
    if (!fileMetadata.fileSize || fileMetadata.fileSize <= 0) {
      throw new Error(`${prefix}: fileSize is required and must be positive`);
    }
    
    if (fileMetadata.fileSize > 10 * 1024 * 1024) { // 10MB
      throw new Error(`${prefix}: File size exceeds 10MB limit`);
    }

    if (!fileMetadata.view_details) {
      throw new Error(`${prefix}: view_details is required`);
    }

    if (!fileMetadata.view_details.arch) {
      throw new Error(`${prefix}: view_details.arch is required`);
    }

    if (!['upper', 'lower'].includes(fileMetadata.view_details.arch)) {
      throw new Error(`${prefix}: view_details.arch must be 'upper' or 'lower'`);
    }
  }

  /**
   * Validate single file data
   * @param {Object} file - File data object
   * @param {string} prefix - Error message prefix
   * @throws {Error} If validation fails
   */
  validateFile(file, prefix = 'File') {
    if (!file.fileName) {
      throw new Error(`${prefix}: fileName is required`);
    }
    
    if (!file.fileType) {
      throw new Error(`${prefix}: fileType is required`);
    }
    
    if (!['image/jpeg', 'image/png'].includes(file.fileType)) {
      throw new Error(`${prefix}: Only JPEG and PNG formats are allowed`);
    }
    
    if (!file.fileSize || file.fileSize <= 0) {
      throw new Error(`${prefix}: fileSize is required and must be positive`);
    }
    
    if (file.fileSize > 10 * 1024 * 1024) { // 10MB
      throw new Error(`${prefix}: File size exceeds 10MB limit`);
    }
  }

  /**
   * Handle API errors with user-friendly messages
   * @param {Error} error - Axios error object
   * @param {string} defaultMessage - Default error message
   */
  handleApiError(error, defaultMessage) {
    const errorMessage = error.response?.data?.message || defaultMessage;
    console.error('ProtocolMedia API Error:', {
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data
    });
    
    // Log for debugging but don't expose sensitive info
    if (process.env.NODE_ENV === 'development') {
      console.error('Full error details:', error);
    }
  }

  /**
   * Create file metadata from File object
   * @param {File} file - Browser File object
   * @param {string} arch - Dental arch (upper/lower)
   * @param {string} surface - Surface type (default: occlusal)
   * @returns {Object} File metadata for API
   */
  createFileMetadata(file, arch = 'upper', surface = 'occlusal') {
    const extension = file.type.split('/')[1];
    const fileName = `${arch}_${Date.now()}.${extension}`;
    
    return {
      fileName: fileName,
      contentType: file.type,
      fileType: file.type,
      fileSize: file.size,
      view_details: {
        arch: arch,
        surface: surface
      }
    };
  }

  /**
   * Create upload request data for batch upload
   * @param {Object} options - Upload options
   * @returns {Object} Upload request data
   */
  createUploadRequest(options) {
    return {
      category: "ProtocolMedia",
      filesMetadata: options.filesMetadata,
      protocol_type: options.protocol_type || "preop_documentation",
      ...(options.labId && { labId: options.labId })
    };
  }
}

export default new ProtocolMediaService();